// Cleaned and Updated App.js (React Router Structure)
import { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import AppLayout from "./pages/AppLayout";
import NotificationProvider from "./NotificationContext/NotificationProvider";
import ProtectedRoute from "./pages/ProtectedRoute";
import SystemRealtimeToast from "./SystemRealtimeToast";
// === Auth Pages ===
import HomePage from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import NotFound from "./pages/NotFound";
import { initAssistiveTooltips } from "./utils/assistiveTooltips";
const lazyNamed = (loader, name) => lazy(() => loader().then((module) => ({ default: module[name] })));
const ScanPage = lazy(() => import("./pages/ScanPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const InboxPage = lazy(() => import("./pages/InboxPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const MeetingRoom = lazy(() => import("./pages/MeetingRoom"));

// Heavy PDF/OCR/Word/Excel engines load only when Studio is opened.
const StudioHome = lazy(() => import("./pages/DocumentStudio/StudioHome"));
const WordEditor = lazy(() => import("./pages/DocumentStudio/WordEditor"));
const SheetEditor = lazy(() => import("./pages/DocumentStudio/SheetEditor"));
const PdfEditor = lazy(() => import("./pages/DocumentStudio/PdfEditor"));
const PresentationViewer = lazy(() => import("./pages/DocumentStudio/PresentationViewer"));
const PresentationEditor = lazy(() => import("./pages/DocumentStudio/PresentationEditor"));
const TemplateLibrary = lazy(() => import("./pages/DocumentStudio/TemplateLibrary"));
const ImportDocument = lazy(() => import("./pages/DocumentStudio/ImportDocument"));
const StudioDocuments = lazy(() => import("./pages/DocumentStudio/StudioDocuments"));

// === ASOB Dashboards (route-level chunk) ===
const loadAsob = () => import("./pages/ASOBDashboard");
const asobDashboards = new Proxy({}, { get: (_target, name) => lazyNamed(loadAsob, name) });
const {
  COMDASOBDashboard,
  DeputyCOMDDashboard,
  SO2OpsTrgDashboard,
  SO3OpsTrgDashboard,
  SO2PersLogDashboard,
  SO3PersLogDashboard,
  SO2CbtIntDashboard,
  MCCDashboard,
  ClerkMainHQDashboard,
  CO20Dashboard,
  BN2IC20Dashboard,
  OPSO20Dashboard,
  Adjutant20Dashboard,
  Clerk20Dashboard,
  CO60Dashboard,
  BN2IC60Dashboard,
  OPSO60Dashboard,
  Adjutant60Dashboard,
  Clerk60Dashboard,
  CO40Dashboard,
  BN2IC40Dashboard,
  OPSO40Dashboard,
  Adjutant40Dashboard,
  Clerk40Dashboard,
  AdminOfficerSQTSDashboard,
  AdjutantSQTSDashboard,
  ClerkSQTSDashboard,
  OCDashboard,
  BSODashboard,
  ClerkCAUDashboard,
} = asobDashboards;


// === OPS Branch ===
// (S02 OPS — OPS Branch dashboard route lives in the OPS section below; the
// dedicated wrapper page was retired by the F2 dashboard-consolidation pass.)
// === INT Branch ===

// === CIS Branch ===

// === SYS Branch ===

// === OOTW Branch ===
// (COL OOTW / SO1 OOTW / S02 OOTW / SO2 Environmental / SO2 Cbt Engr CIMIC dashboards
// are inline BaseDashboard routes now — the dedicated wrapper pages were retired
// by the F2 dashboard-consolidation pass.)

// === Other OPS Roles ===


// === SIGNAL Branch (route-level chunk) ===
const loadSignal = () => import("./pages/SignalDashboard");
const signalDashboards = new Proxy({}, { get: (_target, name) => lazyNamed(loadSignal, name) });
const {
  SignalDispatchClerkDashboard,
  SignalConcernDashboard,
  SignalArchiveClerkDashboard,
  SignalDorcusClerkDashboard,
  SignalChiefClerkBattalionDashboard,
  SignalAdjutantDashboard,
  SignalBN2ICDashboard,
  SignalCODashboard,
  SignalClerkSOSDashboard,
  SignalChiefClerkSOSDashboard,
  SignalAdminOfficerDashboard,
  SignalSICommsDashboard,
  SignalSIITDashboard,
  SignalSITechDashboard,
  SignalChiefInstructorDashboard,
  SignalCommandantDashboard,
  SignalITSquadronClerkDashboard,
  SignalITSquadronOCDashboard,
  SignalCommsSquadronClerkDashboard,
  SignalCommsSquadronOCDashboard,
  SignalRegistryClerkDashboard,
  SignalDorcusClerkKACSDashboard,
  SignalTrainingClerkDashboard,
  SignalChiefClerkKACSDashboard,
  SignalMasterClerkDashboard,
  SignalSO2Dashboard,
  SignalCommanderDashboard,
} = signalDashboards;

// === Clerk Dashboards ===
const ClerkOOTWDashboard = lazy(() => import("./pages/clerk/OotwClerkDashboard"));
const ClerkOPSDashboard = lazy(() => import("./pages/clerk/OpsClerkDashboard"));

// === Chief Clerks ===
const ChiefClerkOTWDashboard = lazy(() => import("./pages/clerk/OotwChiefClerkDashboard"));
const ChiefClerkOPSDashboard = lazy(() => import("./pages/clerk/OpsChiefClerkDashboard"));

// === Base Dashboards ===
const BaseDashboard = lazy(() => import("./pages/BaseDashboard"));
const MasterClerkDashboardBase = lazy(() => import("./pages/MasterClerkDashboard"));
const FOSDashboard = lazy(() => import("./pages/FOSDashboard"));
const DashboardRouter = lazy(() => import("./pages/DashboardRouter"));

function App() {
  useEffect(() => {
    const cleanup = initAssistiveTooltips();
    return cleanup;
  }, []);

  const [currentRole, setCurrentRole] = useState("");
  const [currentSubBranch, setCurrentSubBranch] = useState("");
  const location = useLocation();

  const syncRoleContext = () => {
    setCurrentRole((localStorage.getItem("role") || "").trim());
    setCurrentSubBranch((localStorage.getItem("subBranch") || "").trim());
  };

  useEffect(() => {
    syncRoleContext();
  }, [location.pathname]);

  useEffect(() => {
    window.addEventListener("storage", syncRoleContext);
    return () => window.removeEventListener("storage", syncRoleContext);
  }, []);

  return (
    <NotificationProvider currentRole={currentRole} currentSubBranch={currentSubBranch}>
      <SystemRealtimeToast />
      <Suspense fallback={<div role="status" style={{ minHeight: "45vh", display: "grid", placeItems: "center", color: "#526076", fontWeight: 700 }}>Loading WAVEY workspace…</div>}>
      <Routes>
        <Route element={<AppLayout />}>
          {/* === Auth === */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/meeting" element={<MeetingRoom />} />
          <Route path="/meeting/:code" element={<MeetingRoom />} />
          <Route path="/join/:code" element={<MeetingRoom />} />
          <Route path="/scan" element={<ScanPage />} />

          {/* === Other Public === */}
          <Route path="/admin" element={<Login />} />

          {/* === Admin (Requires Admin Token) === */}
          <Route element={<ProtectedRoute requireAdmin />}>
            <Route path="/admin-dashboard" element={<AdminDashboard />} />
          </Route>

          {/* === Protected (All Auth Users) === */}
          <Route element={<ProtectedRoute />}>
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/search" element={<SearchPage />} />

          {/* === DOCUMENT STUDIO === */}
          <Route path="/studio" element={<StudioHome />} />
          <Route path="/studio/word/:id" element={<WordEditor />} />
          <Route path="/studio/sheet/:id" element={<SheetEditor />} />
          <Route path="/studio/pdf/:id" element={<PdfEditor />} />
          <Route path="/studio/presentation/:id" element={<PresentationViewer />} />
          <Route path="/studio/presentation/:id/edit" element={<PresentationEditor />} />
          <Route path="/studio/templates" element={<TemplateLibrary />} />
          <Route path="/studio/import" element={<ImportDocument />} />
          <Route
            path="/studio/documents"
            element={<StudioDocuments filter="mine" title="My Documents" subtitle="Documents you created in the Studio." />}
          />
          <Route
            path="/studio/drafts"
            element={<StudioDocuments filter="drafts" title="My Drafts" subtitle="Unsubmitted Studio drafts." />}
          />
          <Route
            path="/studio/shared"
            element={<StudioDocuments filter="shared" title="Shared With Me" subtitle="Documents shared with you by others." />}
          />

          {/* === BASE PAGES === */}
          <Route path="/dashboard/base" element={<BaseDashboard />} />
          <Route
            path="/dashboard/master-clerk"
            element={<MasterClerkDashboardBase />}
          />
           <Route path="/dashboard/yos" element={<BaseDashboard role="YOS" />} />
           <Route
             path="/dashboard/ops-branch/cis-sub-branch/fos"
             element={<FOSDashboard />}
           />
           <Route path="/dashboard/ma" element={<BaseDashboard role="MA" />} />
           <Route path="/dashboard/dac" element={<BaseDashboard role="DAC" />} />
           <Route path="/dashboard/comd" element={<BaseDashboard role="COMD" />} />

          {/* === BRANCH HEADS === */}
          <Route
            path="/dashboard/chief-of-branch/ops-branch"
            element={<BaseDashboard role="CHIEF OF BRANCH" branch="ops branch" subBranch="N/A" />}
          />
          <Route
            path="/dashboard/chief-of-branch/logs-branch"
            element={<BaseDashboard role="CHIEF OF BRANCH" branch="logs branch" subBranch="N/A" />}
          />
          <Route
            path="/dashboard/chief-of-branch/pers-branch"
            element={<BaseDashboard role="CHIEF OF BRANCH" branch="pers branch" subBranch="N/A" />}
          />
          <Route
            path="/dashboard/chief-of-branch/trg-branch"
            element={<BaseDashboard role="CHIEF OF BRANCH" branch="trg branch" subBranch="N/A" />}
          />
          <Route
            path="/dashboard/chief-of-branch/med-branch"
            element={<BaseDashboard role="CHIEF OF BRANCH" branch="med branch" subBranch="N/A" />}
          />

          {/* === OPS === */}
          <Route path="/dashboard/so1-ops" element={<BaseDashboard role="SO1 OPS" />} />

          {/* === INT Branch === */}
          <Route
            path="/dashboard/ops-branch/int-sub-branch/so1-int"
            element={<BaseDashboard role="SO1 INT" />}
          />
          <Route
            path="/dashboard/ops-branch/int-sub-branch/so2-int"
            element={<BaseDashboard role="SO2 INT" />}
          />

          {/* === CIS Branch === */}
          <Route
            path="/dashboard/ops-branch/cis-sub-branch/col-cis"
            element={<BaseDashboard role="COL CIS" />}
          />
          <Route
            path="/dashboard/ops-branch/cis-sub-branch/so1-comms"
            element={<BaseDashboard role="SO1 COMMS" />}
          />
          <Route
            path="/dashboard/ops-branch/cis-sub-branch/so1-it"
            element={<BaseDashboard role="SO1 IT" />}
          />
          <Route
            path="/dashboard/ops-branch/cis-sub-branch/so2-comms"
            element={<BaseDashboard role="SO2 COMM" />}
          />
          <Route
            path="/dashboard/ops-branch/cis-sub-branch/so2-it"
            element={<BaseDashboard role="SO2 IT" />}
          />
          <Route
            path="/dashboard/ops-branch/cis-sub-branch/so2-cyber"
            element={<BaseDashboard role="SO2 CYBER" />}
          />
          <Route
            path="/dashboard/ops-branch/cis-sub-branch/clerk"
            element={<MasterClerkDashboardBase role="CLERK" branch="OPS BRANCH" subBranch="CIS SUB BRANCH" />}
          />

          {/* === SYS Branch === */}
          <Route
            path="/dashboard/ops-branch/sys-sub-branch/col-sys"
            element={<BaseDashboard role="COL SYS" />}
          />
          <Route
            path="/dashboard/ops-branch/sys-sub-branch/so1-pso-coe"
            element={<BaseDashboard role="SO1 PSO/COE" />}
          />
          <Route
            path="/dashboard/ops-branch/sys-sub-branch/so2-eqpt-veh-sys"
            element={<BaseDashboard role="SO2 EQPT/VEH SYS" />}
          />
          <Route
            path="/dashboard/ops-branch/sys-sub-branch/so2-wpn-sys"
            element={<BaseDashboard role="SO2 AMMO/WPN SYS" />}
          />
          <Route
            path="/dashboard/ops-branch/sys-sub-branch/clerk"
            element={<MasterClerkDashboardBase role="CLERK" branch="OPS BRANCH" subBranch="SYS SUB BRANCH" />}
          />

          {/* === OOTW Branch === */}
          <Route path="/dashboard/col-ootw" element={<BaseDashboard role="COL OOTW" />} />
          <Route path="/dashboard/so1-ootw" element={<BaseDashboard role="SO1 OOTW" />} />
          <Route path="/dashboard/s02-ootw" element={<BaseDashboard role="S02 OOTW" />} />

          <Route
            path="/dashboard/so2-environmental"
            element={<BaseDashboard role="SO2 ENVIROMENTAL" />}
          />
          <Route
            path="/dashboard/so2-cbt-engr-cimic"
            element={<BaseDashboard role="SO2 CBT ENGR/CIMIC" />}
          />

          {/* === OTHER OPS ROLES === */}
          <Route
            path="/dashboard/ops-branch/ops-sub-branch/coops"
            element={<BaseDashboard role="COOPS" />}
          />
          <Route
            path="/dashboard/ops-branch/ops-sub-branch/col-ops"
            element={<BaseDashboard role="COL OPS" />}
          />
          <Route
            path="/dashboard/ops-branch/ops-sub-branch/s01plans"
            element={<BaseDashboard role="S01PLANS" />}
          />
          <Route
            path="/dashboard/ops-branch/ops-sub-branch/glo"
            element={<BaseDashboard role="GLO" />}
          />
          <Route
            path="/dashboard/ops-branch/ops-sub-branch/so2-w-keeper"
            element={<BaseDashboard role="SO2 W/KEEPER" />}
          />

          <Route
            path="/dashboard/ops-branch/ops-sub-branch/so2-plans"
            element={<BaseDashboard role="SO2 PLANS" />}
          />

          <Route
            path="/dashboard/ops-branch/ops-sub-branch/master-chief"
            element={<BaseDashboard role="MASTER CHIEF" />}
          />

          {/* === CLERKS === */}

          <Route
            path="/dashboard/clerk/ootw-sub-branch"
            element={<ClerkOOTWDashboard />}
          />
          <Route
            path="/dashboard/clerk/ops-sub-branch"
            element={<ClerkOPSDashboard />}
          />

          {/* === CHIEF CLERKS === */}

          <Route
            path="/dashboard/chief-clerk/int-sub-branch"
            element={<MasterClerkDashboardBase role="CHIEF CLERK" branch="OPS BRANCH" subBranch="INT SUB BRANCH" />}
          />
          <Route
            path="/dashboard/chief-clerk/ootw-sub-branch"
            element={<ChiefClerkOTWDashboard />}
          />
          <Route
            path="/dashboard/chief-clerk/ops-sub-branch"
            element={<ChiefClerkOPSDashboard />}
          />


          {/* === SIGNAL BRANCH === */}
          <Route
            path="/dashboard/signal-branch/battalion/concern"
            element={<SignalConcernDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/battalion/archive-clerk"
            element={<SignalArchiveClerkDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/battalion/dispatch-clerk"
            element={<SignalDispatchClerkDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/battalion/dorcus-clerk"
            element={<SignalDorcusClerkDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/battalion/chief-clerk"
            element={<SignalChiefClerkBattalionDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/battalion/adjutant"
            element={<SignalAdjutantDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/battalion/bn-2ic"
            element={<SignalBN2ICDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/battalion/co"
            element={<SignalCODashboard />}
          />

          <Route
            path="/dashboard/signal-branch/sos-branch/clerk"
            element={<SignalClerkSOSDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/sos-branch/chief-clerk"
            element={<SignalChiefClerkSOSDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/sos-branch/admin-officer"
            element={<SignalAdminOfficerDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/sos-branch/si-comms"
            element={<SignalSICommsDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/sos-branch/si-it"
            element={<SignalSIITDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/sos-branch/si-tech"
            element={<SignalSITechDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/sos-branch/chief-instructor"
            element={<SignalChiefInstructorDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/sos-branch/commandant"
            element={<SignalCommandantDashboard />}
          />

          <Route
            path="/dashboard/signal-branch/it-squadron/clerk"
            element={<SignalITSquadronClerkDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/it-squadron/oc"
            element={<SignalITSquadronOCDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/comms-squadron/clerk"
            element={<SignalCommsSquadronClerkDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/comms-squadron/oc"
            element={<SignalCommsSquadronOCDashboard />}
          />

          <Route
            path="/dashboard/signal-branch/kacs/registry-clerk"
            element={<SignalRegistryClerkDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/kacs/dorcus-clerk"
            element={<SignalDorcusClerkKACSDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/kacs/training-clerk"
            element={<SignalTrainingClerkDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/kacs/chief-clerk"
            element={<SignalChiefClerkKACSDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/kacs/master-clerk"
            element={<SignalMasterClerkDashboard />}
          />
          <Route
            path="/dashboard/signal-branch/kacs/so2"
            element={<SignalSO2Dashboard />}
          />
          <Route
            path="/dashboard/signal-branch/kacs/commander"
            element={<SignalCommanderDashboard />}
          />

          {/* === 2ND BRIGADE === */}

          {/* 2 BDE HQ Routes */}
          <Route path="/dashboard/2nd-brigade/2-bde-hq/ber-comd" element={<BaseDashboard role="BDE COMD" />} />
          <Route path="/dashboard/2nd-brigade/2-bde-hq/ber-2ic" element={<BaseDashboard role="BDE 2IC" />} />
          <Route path="/dashboard/2nd-brigade/2-bde-hq/ber-opso" element={<BaseDashboard role="BDE OPSO" />} />
          <Route path="/dashboard/2nd-brigade/2-bde-hq/ber-adjutant" element={<BaseDashboard role="BDE Adjutant" />} />
          <Route path="/dashboard/2nd-brigade/2-bde-hq/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />

          {/* 3KR Routes */}
          <Route path="/dashboard/2nd-brigade/3kr/battalion/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/battalion/registry-clerk" element={<MasterClerkDashboardBase role="Registry Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/battalion/dorcus-clerk" element={<MasterClerkDashboardBase role="Dorcus Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/battalion/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/battalion/adjutant" element={<BaseDashboard role="Adjutant" />} />
          <Route path="/dashboard/2nd-brigade/3kr/battalion/co" element={<BaseDashboard role="CO" />} />
          <Route path="/dashboard/2nd-brigade/3kr/a-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/a-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/a-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/3kr/a-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/3kr/b-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/b-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/b-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/3kr/b-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/3kr/c-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/c-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/c-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/3kr/c-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/3kr/d-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/d-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/d-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/3kr/d-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/3kr/e-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/e-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/3kr/e-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/3kr/e-company/oc" element={<BaseDashboard role="OC" />} />

          {/* 5KR Routes */}
          <Route path="/dashboard/2nd-brigade/5kr/battalion/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/battalion/registry-clerk" element={<MasterClerkDashboardBase role="Registry Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/battalion/dorcus-clerk" element={<MasterClerkDashboardBase role="Dorcus Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/battalion/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/battalion/adjutant" element={<BaseDashboard role="Adjutant" />} />
          <Route path="/dashboard/2nd-brigade/5kr/battalion/co" element={<BaseDashboard role="CO" />} />
          <Route path="/dashboard/2nd-brigade/5kr/a-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/a-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/a-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/5kr/a-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/5kr/b-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/b-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/b-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/5kr/b-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/5kr/c-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/c-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/c-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/5kr/c-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/5kr/d-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/d-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/d-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/5kr/d-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/5kr/e-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/e-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/5kr/e-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/5kr/e-company/oc" element={<BaseDashboard role="OC" />} />

          {/* 9KR Routes */}
          <Route path="/dashboard/2nd-brigade/9kr/battalion/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/battalion/registry-clerk" element={<MasterClerkDashboardBase role="Registry Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/battalion/dorcus-clerk" element={<MasterClerkDashboardBase role="Dorcus Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/battalion/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/battalion/adjutant" element={<BaseDashboard role="Adjutant" />} />
          <Route path="/dashboard/2nd-brigade/9kr/battalion/co" element={<BaseDashboard role="CO" />} />
          <Route path="/dashboard/2nd-brigade/9kr/a-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/a-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/a-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/9kr/a-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/9kr/b-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/b-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/b-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/9kr/b-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/9kr/c-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/c-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/c-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/9kr/c-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/9kr/d-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/d-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/d-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/9kr/d-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/2nd-brigade/9kr/e-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/e-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/9kr/e-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/9kr/e-company/oc" element={<BaseDashboard role="OC" />} />

          {/* 21 TPT COY Routes */}
          <Route path="/dashboard/2nd-brigade/21-tpt-coy/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/21-tpt-coy/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/21-tpt-coy/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/21-tpt-coy/oc" element={<BaseDashboard role="OC" />} />

          {/* 22 FLD WKSP Routes */}
          <Route path="/dashboard/2nd-brigade/22-fld-wksp/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/22-fld-wksp/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/22-fld-wksp/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/22-fld-wksp/oc" element={<BaseDashboard role="OC" />} />

          {/* 23 OCC Routes */}
          <Route path="/dashboard/2nd-brigade/23-occ/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/2nd-brigade/23-occ/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/2nd-brigade/23-occ/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/2nd-brigade/23-occ/oc" element={<BaseDashboard role="OC" />} />

          {/* === 4TH BRIGADE === */}

          {/* 4 BDE HQ Routes */}
          <Route path="/dashboard/4th-brigade/4-bde-hq/ber-comd" element={<BaseDashboard role="BDE COMD" />} />
          <Route path="/dashboard/4th-brigade/4-bde-hq/ber-2ic" element={<BaseDashboard role="BDE 2IC" />} />
          <Route path="/dashboard/4th-brigade/4-bde-hq/ber-opso" element={<BaseDashboard role="BDE OPSO" />} />
          <Route path="/dashboard/4th-brigade/4-bde-hq/ber-adjutant" element={<BaseDashboard role="BDE Adjutant" />} />
          <Route path="/dashboard/4th-brigade/4-bde-hq/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />

          {/* 1KR Routes */}
          <Route path="/dashboard/4th-brigade/1kr/battalion/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/1kr/battalion/registry-clerk" element={<MasterClerkDashboardBase role="Registry Clerk" />} />
          <Route path="/dashboard/4th-brigade/1kr/battalion/dorcus-clerk" element={<MasterClerkDashboardBase role="Dorcus Clerk" />} />
          <Route path="/dashboard/4th-brigade/1kr/battalion/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/1kr/battalion/adjutant" element={<BaseDashboard role="Adjutant" />} />
          <Route path="/dashboard/4th-brigade/1kr/battalion/co" element={<BaseDashboard role="CO" />} />
          <Route path="/dashboard/4th-brigade/1kr/41-occ/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/1kr/41-occ/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/1kr/41-occ/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/1kr/41-occ/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/4th-brigade/1kr/81-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/1kr/81-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/1kr/81-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/1kr/81-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/4th-brigade/1kr/3-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/1kr/3-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/1kr/3-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/1kr/3-company/oc" element={<BaseDashboard role="OC" />} />

          {/* 7KR Routes */}
          <Route path="/dashboard/4th-brigade/7kr/battalion/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/7kr/battalion/registry-clerk" element={<MasterClerkDashboardBase role="Registry Clerk" />} />
          <Route path="/dashboard/4th-brigade/7kr/battalion/dorcus-clerk" element={<MasterClerkDashboardBase role="Dorcus Clerk" />} />
          <Route path="/dashboard/4th-brigade/7kr/battalion/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/7kr/battalion/adjutant" element={<BaseDashboard role="Adjutant" />} />
          <Route path="/dashboard/4th-brigade/7kr/battalion/co" element={<BaseDashboard role="CO" />} />
          <Route path="/dashboard/4th-brigade/7kr/41-occ/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/7kr/41-occ/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/7kr/41-occ/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/7kr/41-occ/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/4th-brigade/7kr/81-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/7kr/81-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/7kr/81-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/7kr/81-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/4th-brigade/7kr/3-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/7kr/3-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/7kr/3-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/7kr/3-company/oc" element={<BaseDashboard role="OC" />} />

          {/* 15KR Routes */}
          <Route path="/dashboard/4th-brigade/15kr/battalion/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/15kr/battalion/registry-clerk" element={<MasterClerkDashboardBase role="Registry Clerk" />} />
          <Route path="/dashboard/4th-brigade/15kr/battalion/dorcus-clerk" element={<MasterClerkDashboardBase role="Dorcus Clerk" />} />
          <Route path="/dashboard/4th-brigade/15kr/battalion/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/15kr/battalion/adjutant" element={<BaseDashboard role="Adjutant" />} />
          <Route path="/dashboard/4th-brigade/15kr/battalion/co" element={<BaseDashboard role="CO" />} />
          <Route path="/dashboard/4th-brigade/15kr/41-occ/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/15kr/41-occ/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/15kr/41-occ/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/15kr/41-occ/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/4th-brigade/15kr/81-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/15kr/81-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/15kr/81-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/15kr/81-company/oc" element={<BaseDashboard role="OC" />} />
          <Route path="/dashboard/4th-brigade/15kr/3-company/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/15kr/3-company/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/15kr/3-company/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/15kr/3-company/oc" element={<BaseDashboard role="OC" />} />

          {/* 41 TPT COY Routes */}
          <Route path="/dashboard/4th-brigade/41-tpt-coy/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/41-tpt-coy/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/41-tpt-coy/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/41-tpt-coy/oc" element={<BaseDashboard role="OC" />} />

          {/* 42 FLD WKSP Routes */}
          <Route path="/dashboard/4th-brigade/42-fld-wksp/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/42-fld-wksp/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/42-fld-wksp/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/42-fld-wksp/oc" element={<BaseDashboard role="OC" />} />

          {/* 43 OCC Routes */}
          <Route path="/dashboard/4th-brigade/43-occ/typing-clerk" element={<MasterClerkDashboardBase role="Typing Clerk" />} />
          <Route path="/dashboard/4th-brigade/43-occ/chief-clerk" element={<MasterClerkDashboardBase role="Chief Clerk" />} />
          <Route path="/dashboard/4th-brigade/43-occ/admin-officer" element={<BaseDashboard role="Admin Officer" />} />
          <Route path="/dashboard/4th-brigade/43-occ/oc" element={<BaseDashboard role="OC" />} />

          {/* Chief Clerk login redirect uses /dashboard/chief-clerk/:subBranch */}
          <Route
            path="/dashboard/chief-clerk/battalion"
            element={<SignalChiefClerkBattalionDashboard />}
          />
          <Route
            path="/dashboard/chief-clerk/sos-branch"
            element={<SignalChiefClerkSOSDashboard />}
          />
          <Route
            path="/dashboard/chief-clerk/kacs"
            element={<SignalChiefClerkKACSDashboard />}
          />

{/* === SOI (No branches: only BaseDashboard + MasterClerkDashboard) === */}
<Route
  path="/dashboard/soi/n-a/commandant"
  element={<BaseDashboard role="Commandant" />}
/>
<Route
  path="/dashboard/soi/n-a/chief-instructor"
  element={<BaseDashboard role="Chief Instructor" />}
/>
<Route
  path="/dashboard/soi/n-a/senior-instructor"
  element={<BaseDashboard role="Senior Instructor" />}
/>
<Route
  path="/dashboard/soi/n-a/commanding-officer"
  element={<BaseDashboard role="Commanding Officer" />}
/>
<Route
  path="/dashboard/soi/n-a/admin-officer"
  element={<BaseDashboard role="Admin Officer" />}
/>
<Route
  path="/dashboard/soi/n-a/officer-commanding"
  element={<BaseDashboard role="Officer Commanding" />}
/>
<Route
  path="/dashboard/soi/n-a/admin-wing"
  element={<BaseDashboard role="Admin Wing" />}
/>

{/* ✅ These 3 share MasterClerkDashboard */}
<Route
  path="/dashboard/soi/n-a/chief-clerk"
  element={<MasterClerkDashboardBase role="Chief Clerk" />}
/>
<Route
  path="/dashboard/soi/n-a/clerk"
  element={<MasterClerkDashboardBase role="Clerk" />}
/>
<Route
  path="/dashboard/soi/n-a/dorcus-clerk"
  element={<MasterClerkDashboardBase role="Dorcus Clerk" />}
/>
<Route
  path="/dashboard/soi/n-a/comcen"
  element={<MasterClerkDashboardBase role="Comcen" />}
/>

          {/* === HQ ASOB / MAIN HQ (Fixed for Login Redirects) === */}
          <Route
            path="/dashboard/main-hq/operations/comd"
            element={<COMDASOBDashboard />}
          />
          <Route
            path="/dashboard/main-hq/operations/deputy-comd"
            element={<DeputyCOMDDashboard />}
          />
          <Route
            path="/dashboard/main-hq/operations/so2-ops-trg"
            element={<SO2OpsTrgDashboard />}
          />
          <Route
            path="/dashboard/main-hq/operations/so3-ops-trg"
            element={<SO3OpsTrgDashboard />}
          />

          {/* ✅ Keep existing slug + add alias with & */}
          <Route
            path="/dashboard/hq-asob/main-hq/so2-pers-log"
            element={<SO2PersLogDashboard />}
          />
          <Route
            path="/dashboard/hq-asob/main-hq/so2-pers-&-log"
            element={<SO2PersLogDashboard />}
          />

          <Route
            path="/dashboard/hq-asob/main-hq/so3-pers-log"
            element={<SO3PersLogDashboard />}
          />
          <Route
            path="/dashboard/hq-asob/main-hq/so2-cbt-int"
            element={<SO2CbtIntDashboard />}
          />
          <Route
            path="/dashboard/main-hq/operations/mcc"
            element={<MCCDashboard />}
          />
          <Route
            path="/dashboard/hq-asob/main-hq/clerk"
            element={<ClerkMainHQDashboard />}
          />

          {/* === Other HQ / Battalions ASOB === */}
          <Route
            path="/hq-20-para-bn/battalion-hq/co"
            element={<CO20Dashboard />}
          />
          <Route
            path="/dashboard/hq-20-para-bn/battalion-hq/bn-2ic"
            element={<BN2IC20Dashboard />}
          />
          <Route
            path="/dashboard/hq-20-para-bn/battalion-hq/opso"
            element={<OPSO20Dashboard />}
          />
          <Route
            path="/dashboard/hq-20-para-bn/battalion-hq/adjutant"
            element={<Adjutant20Dashboard />}
          />
          <Route
            path="/dashboard/hq-20-para-bn/battalion-hq/clerk"
            element={<Clerk20Dashboard />}
          />

          <Route
            path="/dashboard/hq-60-para-bn/battalion-hq/co"
            element={<CO60Dashboard />}
          />
          <Route
            path="/dashboard/hq-60-para-bn/battalion-hq/bn-2ic"
            element={<BN2IC60Dashboard />}
          />
          <Route
            path="/dashboard/hq-60-para-bn/battalion-hq/opso"
            element={<OPSO60Dashboard />}
          />
          <Route
            path="/dashboard/hq-60-para-bn/battalion-hq/adjutant"
            element={<Adjutant60Dashboard />}
          />
          <Route
            path="/dashboard/hq-60-para-bn/battalion-hq/clerk"
            element={<Clerk60Dashboard />}
          />

          <Route
            path="/dashboard/hq-40-ranger/command/co"
            element={<CO40Dashboard />}
          />
          <Route
            path="/dashboard/hq-40-ranger/command/bn-2ic"
            element={<BN2IC40Dashboard />}
          />
          <Route
            path="/dashboard/hq-40-ranger/battalion-hq/opso"
            element={<OPSO40Dashboard />}
          />
          <Route
            path="/dashboard/hq-40-ranger/battalion-hq/adjutant"
            element={<Adjutant40Dashboard />}
          />
          <Route
            path="/dashboard/hq-40-ranger/battalion-hq/clerk"
            element={<Clerk40Dashboard />}
          />

          <Route
            path="/dashboard/hq-sqts/school-hq/admin-officer"
            element={<AdminOfficerSQTSDashboard />}
          />
          <Route
            path="/dashboard/hq-sqts/school-hq/adjutant"
            element={<AdjutantSQTSDashboard />}
          />
          <Route
            path="/dashboard/hq-sqts/school-hq/clerk"
            element={<ClerkSQTSDashboard />}
          />

            <Route path="/dashboard/cau/admin-unit/oc" element={<OCDashboard />} />
            <Route path="/dashboard/cau/admin-unit/bso" element={<BSODashboard />} />
            <Route path="/dashboard/cau/admin-unit/clerk" element={<ClerkCAUDashboard />} />

          {/* === DYNAMIC CATCH-ALL — handles all roles via DashboardRouter === */}
          <Route path="/dashboard/:branch/:subBranch/:role" element={<DashboardRouter />} />
          <Route path="/dashboard/*" element={<DashboardRouter />} />
          </Route>

          {/* === 404 === */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      </Suspense>
    </NotificationProvider>
  );
}

export default App;
